/**
 * Shared date helpers.
 *
 * The system runs in America/Los_Angeles (Pacific). Cloudflare Workers run in
 * UTC; Airtable's `TODAY()` resolves in the base's timezone (GMT unless the
 * base is set otherwise); the browser's `new Date()` reflects whatever the
 * operator's machine thinks "today" is. None of those naturally matches the
 * operator's local day boundary. Both the worker (snooze filter) and the web
 * (row urgency styling) need the same notion of "today as the operator sees
 * it" — that notion lives here so both sides agree at the wire.
 */

/**
 * Today as YYYY-MM-DD in the given IANA timezone. `en-CA` locale formats
 * dates as YYYY-MM-DD natively. Pure function — pass a `now` Date for
 * deterministic tests; defaults to the current clock.
 */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** Today in America/Los_Angeles — the system's operating timezone. */
export function todayInPT(now: Date = new Date()): string {
  return todayIn("America/Los_Angeles", now);
}

/**
 * The Pacific calendar date (YYYY-MM-DD) an arbitrary ISO instant falls on
 * (America/Los_Angeles; DST-correct via Intl). The metrics extractor turns a
 * datetime stamp — a Date Sent / Date Responded / Placed Date instant — into
 * the Pacific day it belongs to (ADR-0151 ruling 1): 2026-07-03T04:00:00Z is
 * Thu 07-02 9pm Pacific → "2026-07-02". Returns null for an unparseable
 * instant. A date-only `YYYY-MM-DD` reads as UTC midnight and returns the day
 * before — pass instants only. The Airtable writers stamp date-only fields
 * through it and `todayInPT` (engineering/contract/dimensions.ts, `day`).
 */
export function dateInPT(isoInstant: string): string | null {
  const t = Date.parse(isoInstant);
  if (Number.isNaN(t)) return null;
  return todayIn("America/Los_Angeles", new Date(t));
}

/**
 * Add `days` (signed) to a YYYY-MM-DD string. Returns the same shape.
 * Anchored at UTC midnight so the arithmetic is timezone-free — date-only in,
 * date-only out. Lead Volume's window math (today minus N, ±1 boundary shims
 * on the Airtable formula) all collapse to this one primitive.
 */
export function addDays(yyyymmdd: string, days: number): string {
  const t = new Date(`${yyyymmdd}T00:00:00Z`).getTime();
  const shifted = new Date(t + days * 86_400_000);
  const y = shifted.getUTCFullYear();
  const m = String(shifted.getUTCMonth() + 1).padStart(2, "0");
  const d = String(shifted.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
